import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import Flatpickr from 'react-flatpickr';
import { useTemplate } from '../../../features/template/TemplateProvider';
import { GraphIcon } from '../../../utils/SVGIcon';
import { ApiDBOverallStatistics } from '../DashboardOperation';

function ContentHead({ handleOnChange, formData }) {
    return (
        <div className="w-full inline-flex md:flex justify-between items-center ">
            <div className="flex items-center gap-x-1 md:gap-x-2 w-[calc(100%_-_192px)]">
                <GraphIcon className="inline-block size-4 xl:size-6 min-w-max" />
                <h4 className="text-primary-700 text-sm 2xl:text-base whitespace-nowrap">
                    Financial Statistics
                </h4>
            </div>
            <Flatpickr
                name="date"
                options={{ mode: 'range' }}
                value={[formData.dateStart, formData.dateEnd]}
                onChange={handleOnChange}
                className="block w-60 xl:w-48 focus:outline-none text-primary-700 !text-xs xl:!text-sm 2xl:text-base !bg-[98%_0px] xl:!bg-[100%_1px] !bg-[length:15px] xl:!bg-[length:17px]"
            />
        </div>
    );
}

function FinancialStatistics() {
    const [dbSalesStatistics, setDbSalesStatistics] = useState([]);
    const { setMessage } = useTemplate();
    const startDate = dayjs().subtract(28, 'day').format('YYYY-MM-DD');
    // add 1 day to current date
    const endDate = dayjs().format('YYYY-MM-DD');
    const [formData, setFormData] = useState({
        dateStart: startDate,
        dateEnd: endDate
    });

    const handleOnChange = (e) => {
        if (e?.length === 2) {
            setFormData({
                dateStart: dayjs(e[0]).format('YYYY-MM-DD') || '',
                dateEnd: dayjs(e[1]).format('YYYY-MM-DD') || ''
            });
        }
    };
    const salesData = useMemo(() => {
        if (dbSalesStatistics?.length > 0) {
            const data = dbSalesStatistics.map((item) => item.sales);
            return data;
        }
        return [];
    }, [dbSalesStatistics]);

    const newCustomersData = useMemo(() => {
        if (dbSalesStatistics?.length > 0) {
            const data = dbSalesStatistics.map((item) => item.newCustomers);
            return data;
        }
        return [];
    }, [dbSalesStatistics]);

    // get array date between start date and end date
    const dateArr = useMemo(() => {
        if (dbSalesStatistics?.length > 0) {
            const data = dbSalesStatistics.map((item) => item.day);
            return data;
        }
        return [];
    }, [dbSalesStatistics]);
    const options = {
        chart: {
            id: 'financial-statistic',
            type: 'area',
            height: 400,
            zoom: {
                enabled: false
            },
            toolbar: {
                show: true
            },
            fontFamily: 'Roboto',
            stacked: true
        },
        dataLabels: {
            enabled: false,
            style: {
                colors: ['#00916E', '#00A3FF']
            }
        },
        stroke: {
            width: 2,
            curve: 'smooth',
            colors: ['#00916E', '#00A3FF'],
            dashArray: [7, 0]
        },
        xaxis: {
            type: 'datetime',
            categories: dateArr,
            labels: {
                formatter(val) {
                    return dayjs(val).format('DD-MMM-YYYY');
                }
            }
        },
        tooltip: {
            x: {
                format: 'dd-MMM-yyyy'
            }
        },
        yaxis: [
            {
                title: {
                    text: 'Sales',
                    style: {
                        color: '#00916E'
                    }
                },
                min: 0,
                labels: {
                    formatter(value) {
                        if (Number.isInteger(value)) {
                            return value;
                        }
                        return '';
                    }
                }
            },
            {
                opposite: true,
                title: {
                    text: 'New Customers',
                    style: {
                        color: '#00A3FF'
                    }
                },
                min: 0,
                labels: {
                    formatter(value) {
                        if (value < 0) {
                            return '';
                        }
                        if (Number.isInteger(value)) {
                            return value;
                        }
                        return '';
                    }
                }
            }
        ],
        legend: {
            horizontalAlign: 'center'
        },
        markers: {
            size: 0.2,
            hover: {
                sizeOffset: 6
            }
        },
        colors: ['#00916E', '#00A3FF'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.5,
                opacityTo: 0.2,
                stops: [0, 100]
            }
        }
    };
    const series = [
        {
            type: 'area',
            name: 'Sales',
            data: salesData
        },
        {
            type: 'area',
            name: 'New Customers',
            data: newCustomersData
        }
    ];

    useEffect(() => {
        ApiDBOverallStatistics(formData)
            .then(({ data: resData }) => {
                setDbSalesStatistics(resData);
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err.toString(0) });
            });
    }, [formData, setMessage]);

    return (
        <div className="p-2 md:px-7 md:pb-3 md:py-6 bg-white">
            <ContentHead handleOnChange={handleOnChange} formData={formData} />
            <div className=" overflow-y-hidden overflow-x-auto scrollbar scrollbar-h-1">
                <div className="mt-2 md:mt-6 h-64 md:h-44 lg:h-56 w-[700px] md:w-[900px] xl:w-full">
                    <Chart
                        options={options}
                        series={series}
                        type="area"
                        width="100%"
                        height="100%"
                    />
                </div>
            </div>
        </div>
    );
}

export default FinancialStatistics;
